import { useContext, useMemo } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import FormControl from '@mui/material/FormControl'
import Grid, { GridProps } from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { CvsAutoComplete } from '@components'
import { modalContext, connectorContext } from '@contexts'
import { useIntegrationAssignment } from '@global/hooks'
import { templateCopyContext } from '../../../../context'

const NoMarginTypography = styled(Typography)`
  margin-top: 0px;
`
const TypographyBold = styled(Typography)`
  weight: 500;
`
const GridMargin = styled(Grid)<GridProps>`
  margin-top: 0;
  margin-bottom: 16px;
  flex-direction: column;
`

interface AccountSelectionProps {
  selectedAccount: string | null
  integrationAssignment: any
  resetOnChange?: boolean
  dataType?: any
  disabled?: boolean
}

export const AccountSelection = ({
  selectedAccount,
  integrationAssignment,
  resetOnChange,
  dataType,
  disabled,
}: AccountSelectionProps) => {
  const ModalContext = useContext(modalContext)
  const { byId } = useContext<any>(connectorContext)
  const { onChange } = useContext<any>(templateCopyContext)

  const { onIntegrationChange, onIntegrationResetByAccountChange } = useIntegrationAssignment({
    integrationAssignment,
    onChange,
  })

  const selectableAccounts = useMemo(
    () =>
      Object.values(byId)
        .filter(
          (connector: any) =>
            connector.connector_type_id ===
            (integrationAssignment.connector_type_id
              ? integrationAssignment.connector_type_id
              : byId[integrationAssignment.connector_id].connector_type_id),
        )
        .map(({ name, id }: any) => ({
          label: name,
          value: id,
        })),
    [integrationAssignment, byId],
  )

  const accountChangeModal = (connectorId: string) => {
    ModalContext.displayModal({
      title: t`Are you sure?`,
      description: t`Changing your selected account will reset all of the fields for this integration.`,
      confirm: () => () => {
        onIntegrationResetByAccountChange(connectorId, 'connector_id', dataType)
      },
    })
  }

  return (
    <GridMargin container component={FormControl}>
      <TypographyBold variant="h4">{t`Account selection`}</TypographyBold>
      <NoMarginTypography variant="body2">
        {t`Select the account this integration should use`}
      </NoMarginTypography>
      <CvsAutoComplete
        margin="dense"
        options={(selectableAccounts as never[]) || []}
        label={t`Account`}
        selected={selectedAccount ?? undefined}
        onSelected={value => {
          if (resetOnChange && selectedAccount !== null) {
            accountChangeModal(value)
          } else {
            onIntegrationChange(value, 'connector_id')
          }
        }}
        error={!selectedAccount ? t`Required` : undefined}
        disabled={disabled || false}
      />
    </GridMargin>
  )
}
